import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as ecModels from '../../../models'
import * as plugin from '../../plugin'

import { getActionsConfig } from './actions'
import { Config } from './config'
import * as effects from './effects'
import * as extraReducers from './extra-reducers'
import type * as models from './models'
import { Selectors } from './selectors'
import { State } from './state'

import AppliedParamsStorage = store.storage.included.SessionStorage
import EditedParamsStorage = store.storage.included.InMemoryStorage

/**
 * TODO:
 */
export class Plugin<
  TTypeParams extends ecModels.TypeParams.Constraint,
> extends plugin.Plugin<
  TTypeParams,
  plugin.Plugin.Id.Standard.CurrentList,
  State<TTypeParams>,
  Selectors<TTypeParams>,
  ReturnType<typeof getActionsConfig<TTypeParams>>
> {
  /**
   * These were used to produce the current list and count of entities
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly appliedParams
  /**
   * These will be used to produce a new current list and count, if/when they
   * are submitted to be applied
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly editedParams

  /**
   * @see {@link Config}
   */
  public readonly config: Readonly<Config<TTypeParams>>

  constructor({ config, collection }: Plugin.constructor.Params<TTypeParams>) {
    super({
      actions: getActionsConfig<TTypeParams>(),
      collection,
      id: plugin.Plugin.Id.Standard.CurrentList,
      initialState: State.INITIAL,
      selectors: () => new Selectors(this),
    })
    this.config = Config.from(config)

    const storageConfig = {
      parent: this.feature,
      properties: {
        descending: store.storage.property(this.config?.descending),
        filters: store.storage.property(this.config?.filters),
        quantity: store.storage.property(this.config?.quantity),
        search: store.storage.property(this.config?.search),
        skip: store.storage.property(this.config?.skip),
        sortBy: store.storage.property(this.config?.sortBy),
      },
    } satisfies
      | AppliedParamsStorage.constructor.Params<
          models.Storage.Config<TTypeParams>
        >
      | EditedParamsStorage.constructor.Params<
          models.Storage.Config<TTypeParams>
        >
    this.appliedParams = new AppliedParamsStorage({
      ...storageConfig,
      name: 'AppliedParams',
    })
    this.editedParams = new EditedParamsStorage({
      ...storageConfig,
      name: 'EditedParams',
    })

    this.feature.registerExtraReducers(
      extraReducers.resetOnContextChange(collection),
    )
    this.feature.registerEffects(
      effects.applyParams(this),
      effects.executeCountMatches(this),
      effects.discardParams(this),
      effects.updateListOnCreation(this),
      effects.executeLoad(this),
      effects.removeOnDeletion(this),
      effects.resetParamsOnContextChange(this),
      effects.startEditingParams(this),
      effects.resetPageOnQueryRequestChange(this),
      effects.triggerCountMatches(this),
      effects.triggerLoad(this),
    )
  }
}
export namespace Plugin {
  export namespace constructor {
    export type Params<TTypeParams extends ecModels.TypeParams.Constraint> =
      Pick<plugin.Plugin.Default<TTypeParams>, 'collection'> & {
        config?: Config.Options<TTypeParams>
      }
  }
}
